import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  GraduationCap,
  LayoutDashboard,
  Users,
  BookOpen,
  Calendar,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronDown,
  School,
  ClipboardList,
  UserCheck,
  BarChart3,
  IndianRupee,
  Upload,
  Building2,
  TrendingUp,
  BookOpenCheck,
  CalendarDays,
  KeyRound,
  History,
  ShieldCheck,
  FileCheck2,
  ListChecks,
  ScrollText,
  BookMarked,
} from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Navigation items based on role
  const getNavItems = () => {
    const role = user?.role;
    
    const commonItems = [
      { icon: LayoutDashboard, label: 'Dashboard', path: `/${role}` }
    ];

    const roleSpecificItems = {
      admin: [
        { icon: Users, label: 'Students', path: '/admin/students' },
        { icon: Upload, label: 'Import Students', path: '/admin/import' },
        { icon: BookOpen, label: 'Teachers', path: '/admin/teachers' },
        { icon: School, label: 'Classes', path: '/admin/classes' },
        { icon: ClipboardList, label: 'Subjects', path: '/admin/subjects' },
        { icon: Calendar, label: 'Attendance', path: '/admin/attendance' },
        { icon: FileText, label: 'Marks', path: '/admin/marks' },
        { icon: BookOpenCheck, label: 'Homework', path: '/admin/homework' },
        { icon: CalendarDays, label: 'Events', path: '/admin/events' },
        { icon: IndianRupee, label: 'Fee Management', path: '/admin/fees' },
        { icon: IndianRupee, label: 'Fee Structure', path: '/admin/fees/structure' },
        { icon: ShieldCheck, label: 'Question Bank', path: '/examinations/question-bank' },
        { icon: FileCheck2, label: 'Question Papers', path: '/examinations/my-papers' },
        { icon: ListChecks, label: 'Approval Queue', path: '/examinations/approval-queue' },
        { icon: BookMarked, label: 'Syllabus', path: '/examinations/syllabus' },
        { icon: ScrollText, label: 'Exam Audit Logs', path: '/examinations/audit-logs' },
        { icon: BarChart3, label: 'Reports', path: '/admin/reports' },
        { icon: KeyRound, label: 'Password Resets', path: '/admin/password-resets' },
        { icon: History, label: 'Password History', path: '/admin/password-history' },
        { icon: Settings, label: 'Settings', path: '/admin/settings' },
      ],
      teacher: [
        { icon: School, label: 'My Classes', path: '/teacher/classes' },
        { icon: Calendar, label: 'Attendance', path: '/teacher/attendance' },
        { icon: FileText, label: 'Marks', path: '/teacher/marks' },
        { icon: BookOpenCheck, label: 'Homework', path: '/teacher/homework' },
        { icon: CalendarDays, label: 'Events', path: '/teacher/events' },
        { icon: Users, label: 'Students', path: '/teacher/students' },
        { icon: ShieldCheck, label: 'Question Bank', path: '/examinations/question-bank' },
        { icon: FileCheck2, label: 'Question Papers', path: '/examinations/my-papers' },
        { icon: ListChecks, label: 'Approval Queue', path: '/examinations/approval-queue' },
        { icon: BookMarked, label: 'Syllabus', path: '/examinations/syllabus' },
        { icon: ScrollText, label: 'Exam Audit Logs', path: '/examinations/audit-logs' },
      ],
      student: [
        { icon: Calendar, label: 'Attendance', path: '/student/attendance' },
        { icon: FileText, label: 'Marks', path: '/student/marks' },
        { icon: BookOpenCheck, label: 'Homework', path: '/student/homework' },
        { icon: CalendarDays, label: 'Events', path: '/student/events' },
        { icon: ClipboardList, label: 'Marksheets', path: '/student/marksheets' },
      ],
      parent: [
        { icon: Users, label: 'Children', path: '/parent/children' },
        { icon: Calendar, label: 'Attendance', path: '/parent/attendance' },
        { icon: FileText, label: 'Marks', path: '/parent/marks' },
        { icon: BookOpenCheck, label: 'Homework', path: '/parent/homework' },
        { icon: CalendarDays, label: 'Events', path: '/parent/events' },
      ],
      superadmin: [
        { icon: Building2, label: 'Schools', path: '/superadmin/schools' },
        { icon: TrendingUp, label: 'Revenue', path: '/superadmin/revenue' },
        { icon: FileText, label: 'Invoices', path: '/superadmin/invoices' },
        { icon: FileText, label: 'Quotations', path: '/superadmin/quotations' },
        { icon: Settings, label: 'Vendor Settings', path: '/superadmin/vendor-settings' },
      ]
    };

    return [...commonItems, ...(roleSpecificItems[role] || [])];
  };

  const navItems = getNavItems();
  const isActivePath = (path) => location.pathname === path;

  const avatarImages = {
    admin: 'https://images.pexels.com/photos/8423388/pexels-photo-8423388.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    teacher: 'https://images.unsplash.com/photo-1758685848177-93817e9ad5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwdGVhY2hlciUyMHBvcnRyYWl0JTIwYXZhdGFyfGVufDB8fHx8MTc3NTA3NzQxMHww&ixlib=rb-4.1.0&q=85',
    student: 'https://images.unsplash.com/photo-1758685848426-fcff62c9fd64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxzdHVkZW50JTIwdGVhY2hlciUyMHBvcnRyYWl0JTIwYXZhdGFyfGVufDB8fHx8MTc3NTA3NzQxMHww&ixlib=rb-4.1.0&q=85',
    parent: 'https://images.unsplash.com/photo-1758685848177-93817e9ad5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwdGVhY2hlciUyMHBvcnRyYWl0JTIwYXZhdGFyfGVufDB8fHx8MTc3NTA3NzQxMHww&ixlib=rb-4.1.0&q=85'
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - Desktop */}
      <aside className="fixed left-0 top-0 z-40 hidden lg:flex h-screen w-64 flex-col border-r border-border bg-card">
        {/* Logo */}
        <div className="flex h-16 items-center gap-2 px-6 border-b border-border">
          <GraduationCap className="h-8 w-8 text-primary" />
          <span className="text-xl font-heading font-bold">EduTrack</span>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActivePath(item.path) ? 'active' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 bg-white">
              {/* Prefer the school's uploaded logo. Fall back to the role-based
                  avatar so superadmin (no school) still gets a valid image. */}
              <AvatarImage
                src={
                  user?.school?.logo_url
                    ? (user.school.logo_url.startsWith('http')
                        ? user.school.logo_url
                        : `${process.env.REACT_APP_BACKEND_URL || ''}${user.school.logo_url}`)
                    : avatarImages[user?.role]
                }
                alt={user?.school?.name || 'logo'}
                className="object-contain p-1"
              />
              <AvatarFallback>{(user?.school?.name || user?.name || 'U').charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 h-full w-64 bg-card border-r border-border">
            <div className="flex h-16 items-center justify-between px-6 border-b border-border">
              <div className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-primary" />
                <span className="text-xl font-heading font-bold">EduTrack</span>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <ScrollArea className="flex-1 px-3 py-4 h-[calc(100vh-4rem)]">
              <nav className="space-y-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-link ${isActivePath(item.path) ? 'active' : ''}`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </nav>
            </ScrollArea>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 glass-header h-16 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="hidden sm:block">
              <h1 className="text-lg font-heading font-semibold capitalize">
                {location.pathname.split('/').pop()?.replace(/-/g, ' ') || 'Dashboard'}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-button"
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-button">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={
                      user?.school?.logo_url
                        ? `${process.env.REACT_APP_BACKEND_URL}${user.school.logo_url}`
                        : avatarImages[user?.role]
                    } />
                    <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                  </Avatar>
                  <span className="hidden md:block text-sm">{user?.name}</span>
                  <ChevronDown className="h-4 w-4 hidden md:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.name}</span>
                    <span className="text-xs text-muted-foreground">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/change-password')}
                  data-testid="change-password-menu-item"
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
